import { useForceUpdate } from 'signer-app/utils/use-force-update';
import React from 'react';
import { HyperlinkField, TextField } from 'signer-app/types/editor-types';
import { DEFAULT_FONT_SIZE, MIN_FONT_SIZE } from 'signer-app/signature-request';
import { FieldUpdate } from 'signer-app/signer-signature-document/types';
import { useFeatureFlag } from 'signer-app/context/feature-flags';

export function isMultilineField(field: TextField | HyperlinkField): boolean {
  return (
    field.height >=
    (field.originalFontSize || field.fontSize || DEFAULT_FONT_SIZE) * 2
  );
}

export function getLines(hiddenDOM: HTMLTextAreaElement, value: string) {
  const contentLines = [];
  const lines = value.replace(/\r/g, '').split('\n');

  const { height, minHeight } = hiddenDOM.style;
  hiddenDOM.style.height = '0';
  hiddenDOM.style.minHeight = '0';

  hiddenDOM.value = 'A';
  // The `*1.75` was added to account for Chinese characters.  singleLineHeight
  // is measured against the latter `A`, but Chinese characters can be taller
  // than `A`, which was causing an infinite loop below
  const singleLineHeight = hiddenDOM.scrollHeight * 1.75;

  for (let i = 0; i < lines.length; i++) {
    const words = lines[i].split(' ');
    const line = [];

    while (words.length > 0) {
      line.push(words.shift());
      hiddenDOM.value = line.join(' ');

      // If (it looks like the hiddenDOM is line wrapped) {
      if (hiddenDOM.scrollHeight >= singleLineHeight * 1) {
        // If a single "word" is too wide, cut letters until it fits
        if (line.length === 1) {
          let left;
          // line definitely contains something, because the first line of the
          // while loop pushes to it.
          let right = line[0]!;

          while (right.length > 0) {
            left = right;
            right = '';

            hiddenDOM.value = left;
            while (
              hiddenDOM.scrollHeight > singleLineHeight &&
              left.length > 0
            ) {
              right = left.substr(-1) + right;
              left = left.substr(0, left.length - 1);
              hiddenDOM.value = left;
            }
            if (left.length === 0) {
              throw new Error('Unable to trim input');
            }
            contentLines.push(left);
          }
          line.length = 0;
        } else {
          const lastWord = line.pop();
          contentLines.push(line.join(' '));
          line.length = 0;
          line.push(lastWord);
        }
      }
    }
    contentLines.push(line.join(' '));
  }

  hiddenDOM.style.height = height;
  hiddenDOM.style.minHeight = minHeight;

  return contentLines.join('\n').trim();
}

// Minimal font size which makes mobile browser not zoom in on the field
const MOBILE_ZOOM_FIX_FONT_SIZE = 16;

export function getMobileAdjustments(
  fontSize: number,
  shouldScaleTextField: boolean,
): {
  fontSize: number;
  scale: number;
  sizeScaleFactor: number;
} {
  if (!shouldScaleTextField) {
    return { fontSize, scale: 1, sizeScaleFactor: 1 };
  }

  const scale = fontSize / MOBILE_ZOOM_FIX_FONT_SIZE;
  return {
    fontSize: MOBILE_ZOOM_FIX_FONT_SIZE,
    scale,
    sizeScaleFactor: 1 / scale,
  };
}

function getAdjustedLength(length: number, sizeScaleFactor: number) {
  return Math.round(length * sizeScaleFactor);
}

function isOverflowing(
  hiddenDOM: HTMLTextAreaElement,
  textDOM: HTMLTextAreaElement,
  isMultiline: boolean | undefined,
  sizeScaleFactor: number,
): boolean {
  return (
    (isMultiline &&
      getAdjustedLength(hiddenDOM.scrollHeight, sizeScaleFactor) >
        textDOM.clientHeight) ||
    (!isMultiline &&
      getAdjustedLength(hiddenDOM.scrollWidth, sizeScaleFactor) >
        textDOM.clientWidth)
  );
}

export function calcFontSizeAdjustment(
  fontSize: number,
  hiddenDOM: HTMLTextAreaElement,
  textDOM: HTMLTextAreaElement,
  isMultiline: boolean | undefined,
  shouldScaleTextField: boolean,
) {
  let newSize = fontSize;
  // Shrink the font size until everything fits or we reach the minimum size
  hiddenDOM.style.fontSize = `${fontSize}px`;

  const { sizeScaleFactor } = getMobileAdjustments(
    fontSize,
    shouldScaleTextField,
  );

  while (
    newSize > MIN_FONT_SIZE &&
    isOverflowing(hiddenDOM, textDOM, isMultiline, sizeScaleFactor)
  ) {
    newSize--;
    const { scrollHeight: oldHeight, scrollWidth: oldWidth } = hiddenDOM;
    hiddenDOM.style.fontSize = `${newSize}px`;
    // in some cases (ex: CJK fonts on CJK systems) a lower fontSize value doesn't change the
    // rendered font size. This early break is meant to catch these scenarios and prevent other
    // issues downstream (ex: fontSize being reduced to MIN_FONT_SIZE and isMultiline returning
    // true when the real font size is 11px)
    if (
      oldHeight ===
        getAdjustedLength(hiddenDOM.scrollHeight, sizeScaleFactor) &&
      oldWidth === getAdjustedLength(hiddenDOM.scrollWidth, sizeScaleFactor)
    ) {
      newSize += 1;
      break;
    }
  }

  return newSize;
}

const newLine = /\n/g;

export function trimOverflow(
  value: string,
  hiddenDOM: HTMLTextAreaElement,
  textDOM: HTMLTextAreaElement,
  isMultiline: boolean | undefined,
  fontSize: number,
  shouldScaleTextField: boolean,
) {
  const { sizeScaleFactor } = getMobileAdjustments(
    fontSize,
    shouldScaleTextField,
  );
  // Remove 1 character at a time until everything fits.
  let trimmedValue = value;
  if (!isMultiline && trimmedValue.indexOf('\n') >= 0) {
    // SIGN-10406 - Without this line, you can add newlines to a single-line
    // merge field, and not get an error.
    trimmedValue = trimmedValue.replace(newLine, ' ');
  }
  while (
    trimmedValue.length > 1 &&
    isOverflowing(hiddenDOM, textDOM, isMultiline, sizeScaleFactor)
  ) {
    trimmedValue = trimmedValue.substr(0, trimmedValue.length - 1);
    hiddenDOM.value = trimmedValue;
  }
  return trimmedValue;
}

export function useSignerTextInput(
  fieldData: TextField,
  textRef: undefined | React.RefObject<HTMLTextAreaElement | undefined>,
  // WARNING: hidden assumes it has a text field that is styled exactly the same
  // as textRef (ie fontSize, fontFamily, margin, padding, etc), except that it
  // has 0 width and height and is hidden.
  hidden: undefined | React.RefObject<HTMLTextAreaElement | undefined>,
  onChange: (updates: FieldUpdate) => void,
  locationName: 'review' | 'preview' | 'signer' | 'overlay',
  canTrim = true,
) {
  const forceUpdate = useForceUpdate();
  React.useLayoutEffect(() => {
    // On the first render `textRef` and `hidden` refs aren't available. This
    // force update will quickly force a next render so the text can be
    // adjusted.
    forceUpdate();
  }, [forceUpdate]);
  const [isOverflowing, setOverflow] = React.useState(false);
  const { name } = fieldData;
  const previousValue = React.useRef<string>('');
  const shouldScaleTextField = useFeatureFlag(
    'sign_core_2025_11_20_text_field_scaling',
  );

  /**
   * If the original field was designed to be at least twice as tall as its font
   * size, it is a multiline field.
   *
   * If not, then the intention has always been that it is a single-line field
   * and should shrink the font, but not wrap.
   *
   * There was a bug where shrinking the font could lead to becoming half the
   * size of the field and becoming a multiline field. When fields are resized,
   * an `originalFontSize` property is added, so now isMultlineField uses that.
   */
  const canWrapText = isMultilineField(fieldData);
  React.useEffect(() => {
    let value = fieldData.value || '';
    const originalValue = value;
    if (!textRef || !textRef.current || !hidden || !hidden.current) {
      return;
    }
    const textDOM = textRef.current;
    const hiddenDOM = hidden.current;
    // If we have the same or more text as the previous render, begin fontSize adjustments from
    // fieldData.fontSize to prevent subsequent calls from turning cyclic (ex: 7 -> 8 -> 7, etc.).
    // If we have less content (backspace/input clear), begin from fieldData.originalFontSize in
    // case the user clears the input.
    const startingFontSize =
      value.indexOf(previousValue.current) === 0
        ? fieldData.fontSize || fieldData.originalFontSize
        : fieldData.originalFontSize;
    const fontSize = startingFontSize || DEFAULT_FONT_SIZE;

    const newFontSize = calcFontSizeAdjustment(
      fontSize,
      hiddenDOM,
      textDOM,
      canWrapText,
      shouldScaleTextField,
    );

    const trimmedValue = trimOverflow(
      value,
      hiddenDOM,
      textDOM,
      canWrapText,
      fontSize,
      shouldScaleTextField,
    );
    if (canTrim) {
      value = trimmedValue;
    }
    const lines = canWrapText ? getLines(hiddenDOM, value) : value;

    const updates: Partial<Pick<TextField, 'value' | 'lines' | 'fontSize'>> =
      {};
    if (value !== originalValue) {
      updates.value = value;
    }
    /**
     * Only update lines if it is different from its previous value.
     *
     * "value" is directly manipulated by the user. It can be a multi-line field
     * where the user has injected newlines manually. Or it could be a multiline
     * field that needs to be wrapped. Or it could be a single-line field that
     * doesn't allow newlines and instead, shrinks the font to its minimum size.
     *
     * "lines" is that same text, but with "\n" injected to wrap the text within
     * its field.  Our backend needs "lines", so it should be considered the
     * value of the field. I think having the newlines injected directly into
     * the value in the textbox can make it awkward to edit.
     *
     * 1 | line with some   |
     * 2 | text that is too |
     * 3 | long to fit      |
     *
     * What happens when you delete the word "line" from line 1?
     * If there are newlines, then lines 2 and 3 remain the same.
     * If there are no newlines, then "text" is going to reflow up to line 1.
     *
     *
     * const fieldValue = field.lines || field.value;
     */
    if (lines !== (fieldData.lines || '')) {
      updates.lines = lines;
    }
    if (newFontSize !== fieldData.fontSize) {
      updates.fontSize = newFontSize;
    }
    if (Object.keys(updates).length > 0) {
      onChange(updates);
    }
    const overflow = trimmedValue !== value;
    if (overflow !== isOverflowing) {
      setOverflow(overflow);
    }
    previousValue.current = value;
  }, [
    canTrim,
    fieldData.fontSize,
    fieldData.id,
    isOverflowing,
    canWrapText,
    fieldData.lines,
    fieldData.originalFontSize,
    fieldData.value,
    hidden,
    locationName,
    name,
    onChange,
    textRef,
    shouldScaleTextField,
  ]);

  return isOverflowing;
}
